/**
 * Active cart management API service
 * 
 * Provides methods for accessing active customer carts,
 * cart details, and customer-specific cart information.
 */
import apiClient from './api';
import {
  CartDetail,
  PaginatedActiveCartsResponse,
  CartFilters
} from '../types/activeCart';

export class CartService {
  /**
   * Get active carts with optional filters and pagination
   * 
   * @param filters - Filter parameters including customer_type, value range, age range, pagination
   * @returns Paginated list of active carts with customer and cart summary
   */
  static async getActiveCarts(filters?: CartFilters): Promise<PaginatedActiveCartsResponse> {
    try {
      const response = await apiClient.get('/admin/carts/active', {
        params: {
          customer_type: filters?.customer_type,
          min_value: filters?.min_value,
          max_value: filters?.max_value,
          min_age_hours: filters?.min_age_hours,
          max_age_hours: filters?.max_age_hours,
          page: filters?.page || 1,
          page_size: filters?.page_size || 20,
          sort_by: filters?.sort_by,
          sort_order: filters?.sort_order
        }
      });
      return response.data;
    } catch (error) {
      console.error('[CartService] Error fetching active carts:', error);
      throw error;
    }
  }

  /**
   * Get detailed cart information including items and stock availability
   * 
   * @param cartId - Cart ID to retrieve details for
   * @returns Detailed cart information with items, totals, and stock status
   */
  static async getCartDetails(cartId: string): Promise<CartDetail> {
    try {
      const response = await apiClient.get(`/admin/carts/${cartId}/details`);
      return response.data;
    } catch (error) {
      console.error('[CartService] Error fetching cart details:', error);
      throw error;
    }
  }

  /**
   * Get customer's active cart
   * 
   * @param customerId - Customer ID to retrieve cart for
   * @returns Customer's active cart details or null if no active cart
   */
  static async getCustomerCart(customerId: string): Promise<CartDetail | null> {
    try {
      const response = await apiClient.get(`/admin/customers/${customerId}/cart`);
      return response.data;
    } catch (error: any) {
      // Handle 404 as no active cart (return null)
      if (error?.response?.status === 404) {
        return null;
      }
      console.error('[CartService] Error fetching customer cart:', error);
      throw error;
    }
  }

  /**
   * Export active carts to CSV
   * 
   * @param filters - Filter parameters to apply to export
   * @returns Blob containing CSV data
   */
  static async exportActiveCarts(filters?: CartFilters): Promise<Blob> {
    try {
      const response = await apiClient.get('/admin/carts/active/export', {
        params: {
          customer_type: filters?.customer_type,
          min_value: filters?.min_value,
          max_value: filters?.max_value,
          min_age_hours: filters?.min_age_hours,
          max_age_hours: filters?.max_age_hours
        },
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('[CartService] Error exporting active carts:', error);
      throw error;
    }
  }

  /**
   * Get executive summary statistics for active carts
   */
  static async getSummaryStats(): Promise<{
    total_active_carts: number;
    total_stranded_value: number;
    average_cart_value: number;
    hot_carts_count: number;
    warm_carts_count: number;
    stale_carts_count: number;
    high_value_wholesaler_count: number;
  }> {
    try {
      const response = await apiClient.get('/admin/carts/summary-stats');
      return response.data;
    } catch (error) {
      console.error('[CartService] Error fetching summary stats:', error);
      throw error;
    }
  }

  /**
   * Generate customized WhatsApp recovery message and direct wa.me link
   */
  static async getWhatsAppPreview(cartId: string): Promise<{
    customer_name: string;
    phone: string;
    total_value: number;
    message: string;
    whatsapp_url: string | null;
  }> {
    try {
      const response = await apiClient.post(`/admin/carts/${cartId}/whatsapp-preview`);
      return response.data;
    } catch (error) {
      console.error('[CartService] Error generating WhatsApp preview:', error);
      throw error;
    }
  }

  /**
   * Send direct recovery SMS via TrueBulkSMS
   */
  static async sendSMS(cartId: string, message?: string, phone?: string): Promise<{
    status: string;
    recipient: string;
    message: string;
    details: any;
  }> {
    try {
      const response = await apiClient.post(`/admin/carts/${cartId}/send-sms`, {
        message,
        phone,
      });
      return response.data;
    } catch (error) {
      console.error('[CartService] Error sending SMS:', error);
      throw error;
    }
  }
}


